/**
 * File upload API — mirrors the HAI assistants UI upload flow.
 *
 * Uploads files to the workspace session folder on the backend via
 * POST /workspaces/{id}/upload/file (multipart/form-data).
 *
 * For new chats (no sessionId yet), files go to a "tmp" folder.
 * The backend renames tmp → /sessions/{sessionId}/ once the session is created.
 */
import { apiFetch } from './client';
import { endpoints } from '../config/endpoints';

export interface UploadedFileResult {
  name: string;
  originalName?: string;
  path: string;
  storagePath: string;
  size: number;
  lastModified: string;
  skipped?: boolean;
  renamed?: boolean;
}

export interface UploadFileResponse {
  success?: boolean;
  data?: {
    file: UploadedFileResult;
    skipped?: boolean;
  };
  file?: UploadedFileResult;
}

export interface UploadedAttachment {
  filename: string;
  path: string;
}

// Storage path constants — must match HAI assistants backend
const STORAGE_PREFIX = 'shared_workspace/HAI-ASSISTANTS-WORKSPACES';
const SESSIONS_DIRECTORY = 'sessions';

/**
 * Upload a single file to the workspace session folder.
 */
export async function uploadFileToWorkspace(
  workspaceId: string,
  file: File,
  sessionId?: string,
): Promise<UploadedFileResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('path', file.name);
  formData.append('conflictStrategy', 'rename');
  // 'tmp' for new chats — backend renames to actual sessionId on session creation
  formData.append('sessionId', sessionId || 'tmp');

  const response = await apiFetch(endpoints.workspaces.uploadFile(workspaceId), {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Upload failed (${response.status}): ${text || response.statusText}`);
  }

  const json = await response.json();
  // Backend wraps in { data: { file } } or { file } depending on reply helper
  const result: UploadedFileResult = json?.data?.file ?? json?.file;

  if (!result) {
    throw new Error(`Upload succeeded but returned no file metadata for ${file.name}`);
  }

  return result;
}

/**
 * Upload multiple files and return attachment metadata for XML markup.
 */
export async function uploadAttachmentsForPrompt(
  workspaceId: string,
  files: File[],
  sessionId?: string,
): Promise<UploadedAttachment[]> {
  if (!files.length) return [];

  const uploaded: UploadedAttachment[] = [];

  for (const file of files) {
    const result = await uploadFileToWorkspace(workspaceId, file, sessionId);

    if (!result.storagePath) {
      throw new Error(`Storage path missing for uploaded file: ${file.name}`);
    }

    uploaded.push({
      filename: result.originalName ?? result.name,
      path: buildAttachmentStoragePath(result.storagePath, result.name),
    });
  }

  return uploaded;
}

// ─── XML Markup (matches HAI assistants chat-message-processor.ts) ───

function escapeXml(value: string): string {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Build the <attachments> XML markup appended to the humanPrompt.
 */
export function buildAttachmentPromptSection(attachments: UploadedAttachment[]): string {
  if (!attachments.length) return '';
  const rows = attachments
    .map(
      (file) =>
        `  <attachment filename="${escapeXml(file.filename)}" path="${escapeXml(file.path)}" />`,
    )
    .join('\n');

  return `\n\n<attachments>\n${rows}\n</attachments>`;
}

// ─── Path Helpers ───

function normalizePath(value: string): string {
  return value.replace(/\\/g, '/').replace(/\/{2,}/g, '/');
}

function parseStorageRootSegments(
  storagePath?: string,
): { userId?: string; workspaceId?: string; sessionId?: string } {
  if (!storagePath) return {};
  const normalized = normalizePath(storagePath);
  const trimmed = normalized.replace(/^\/+/, '');
  if (!trimmed) return {};
  const segments = trimmed.split('/');
  if (segments.length < 2) return {};
  const [userId, workspaceId, ...rest] = segments;
  if (!userId || !workspaceId) return {};

  const sessionIndex = rest.findIndex((segment) => segment === SESSIONS_DIRECTORY);
  const sessionId =
    sessionIndex >= 0 && rest.length > sessionIndex + 1 ? rest[sessionIndex + 1] : undefined;

  return { userId, workspaceId, sessionId };
}

function buildAttachmentStoragePath(storagePath: string, filename: string): string {
  const { userId, workspaceId, sessionId } = parseStorageRootSegments(storagePath);

  if (!userId || !workspaceId || !sessionId) {
    throw new Error('Cannot build attachment path: missing userId, workspaceId, or sessionId');
  }

  return normalizePath(
    `${STORAGE_PREFIX}/${userId}/${workspaceId}/${SESSIONS_DIRECTORY}/${sessionId}/${filename}`,
  );
}
